/**
 * Componentes de todos os projetos — o "Componentes Exportar" do menu antigo.
 *
 * **A lista aqui é para conferir o recorte; o CSV é o produto.** A tela antiga
 * despeja os 64.631 componentes numa tabela HTML só e deixa o DataTables montar
 * o arquivo no navegador. Aqui a listagem vem de 100 em 100 e o arquivo sai do
 * banco direto para o disco, em streaming — o recorte inteiro leva 2,5 s e 6,1 MB.
 *
 * **O filtro é o mesmo da caixa de busca do DataTables**: procura ao mesmo tempo
 * na descrição, no apelido, no código e no nome do projeto. Digitar só números
 * também acha o componente pelo id.
 *
 * **O que estiver filtrado é o que sai no arquivo.** Não existe "exportar a
 * página": o botão leva o recorte inteiro, que é o gesto que a tela antiga tinha.
 */

import { useState } from "react"
import { Link } from "react-router-dom"
import {
  usarComponentesDeTodosOsProjetos, usarExportarComponentes,
  type FiltroDeComponentes,
} from "@/api/componentesDeTodosOsProjetos"
import { Faixa } from "@/kit/Faixa"
import { BarraDeEstado } from "@/kit/BarraDeEstado"
import { Botao, estiloCampo, mensagem } from "./pecas"

const VAZIO: FiltroDeComponentes = { projetoId: 0, termo: "" }

export function ComponentesExportar() {
  // Dois estados de propósito: o que está digitado e o que está valendo. Sem
  // isso cada tecla dispararia uma consulta que varre 64 mil linhas.
  const [rascunho, setRascunho] = useState<FiltroDeComponentes>(VAZIO)
  const [filtro, setFiltro] = useState<FiltroDeComponentes>(VAZIO)
  const [pagina, setPagina] = useState(1)
  const [recado, setRecado] = useState<string | null>(null)

  const lista = usarComponentesDeTodosOsProjetos(filtro, pagina)
  const exportar = usarExportarComponentes()

  const total = lista.data?.total ?? 0
  const porPagina = lista.data?.porPagina ?? 100
  const paginas = Math.max(1, Math.ceil(total / porPagina))
  const filtrando = filtro.projetoId > 0 || filtro.termo.trim() !== ""

  function aplicar() {
    setPagina(1)
    setFiltro(rascunho)
    setRecado(null)
  }

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <Faixa
        titulo="Componentes de todos os projetos"
        detalhe={lista.isPending ? "contando…" : `${total.toLocaleString("pt-BR")} ${filtrando ? "no filtro" : "no total"}`}
      >
        <Botao
          destaque
          ocupado={exportar.isPending}
          titulo="Baixa em CSV o recorte inteiro, não só esta página"
          aoClicar={() => exportar.mutate(filtro, {
            onSuccess: (nome) => setRecado(`Baixado: ${nome}`),
            onError: (e) => setRecado(mensagem(e)),
          })}
        >
          {exportar.isPending ? "Gerando…" : "Exportar CSV"}
        </Botao>
      </Faixa>

      <form
        onSubmit={(e) => { e.preventDefault(); aplicar() }}
        style={{
          flex: "0 0 auto", display: "flex", alignItems: "flex-end", gap: 10, flexWrap: "wrap",
          padding: "10px 18px", background: "var(--surface2)", borderBottom: "1px solid var(--line2)",
        }}
      >
        <label style={{ display: "flex", flexDirection: "column", gap: 3, flex: "1 1 280px", maxWidth: 420 }}>
          <span style={rotulo}>Procurar</span>
          <input
            value={rascunho.termo}
            onChange={(e) => setRascunho({ ...rascunho, termo: e.target.value })}
            placeholder="descrição, apelido, código, projeto ou id"
            style={estiloCampo}
          />
        </label>

        <label style={{ display: "flex", flexDirection: "column", gap: 3, width: 130 }}>
          <span style={rotulo}>Projeto</span>
          <input
            type="number"
            value={rascunho.projetoId === 0 ? "" : rascunho.projetoId}
            onChange={(e) => setRascunho({ ...rascunho, projetoId: Number(e.target.value) || 0 })}
            placeholder="todos"
            style={estiloCampo}
          />
        </label>

        <Botao envia aoClicar={aplicar}>Aplicar</Botao>
        {filtrando && (
          <Botao aoClicar={() => { setRascunho(VAZIO); setFiltro(VAZIO); setPagina(1) }}>
            Limpar
          </Botao>
        )}

        <span style={{ fontSize: 10.5, color: "var(--txt3)", lineHeight: 1.5, maxWidth: "46ch" }}>
          O arquivo leva o recorte inteiro — o que estiver filtrado aqui, não só esta página.
        </span>
      </form>

      <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        {lista.isPending ? (
          <p style={{ padding: 16, fontSize: 12, color: "var(--txt3)" }}>Carregando…</p>
        ) : lista.isError ? (
          <p style={{ padding: 16, fontSize: 12, color: "var(--erro)" }}>{mensagem(lista.error)}</p>
        ) : total === 0 ? (
          <p style={{ padding: 16, fontSize: 12, color: "var(--txt3)" }}>
            Nenhum componente atende ao filtro.
          </p>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11.5 }}>
            <thead>
              <tr>
                {["Componente", "Projeto", "Descrição", "Apelido", "Código"].map((r, i) => (
                  <th key={r} style={{
                    position: "sticky", top: 0, zIndex: 1, textAlign: "left",
                    padding: "6px 10px", background: "var(--surface2)",
                    borderBottom: "1px solid var(--line2)", fontSize: 10.5,
                    textTransform: "uppercase", letterSpacing: .4, color: "var(--txt3)",
                    width: i === 0 ? 110 : i === 1 ? 260 : undefined,
                  }}>
                    {r}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(lista.data?.itens ?? []).map((c) => (
                <tr key={c.componenteId}>
                  <td style={{ ...celula, fontFamily: "var(--fm)" }}>{c.componenteId}</td>
                  <td style={celula}>
                    <Link
                      to={`/projetos/${c.projetoId}/cadastros`}
                      style={{ color: "var(--brand)", textDecoration: "none" }}
                    >
                      <span style={{ fontFamily: "var(--fm)" }}>{c.projetoId}</span>{" "}
                      {c.projetoDescricao}
                    </Link>
                  </td>
                  <td style={celula}>{c.descricao}</td>
                  <td style={{ ...celula, color: c.apelido === "" ? "var(--txt3)" : undefined }}>
                    {c.apelido === "" ? "—" : c.apelido}
                  </td>
                  <td style={{ ...celula, fontFamily: "var(--fm)" }}>{c.codigo}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <BarraDeEstado
        direita={
          paginas > 1 ? (
            <span style={{ display: "flex", alignItems: "center", gap: 7 }}>
              <Botao desligado={pagina <= 1} aoClicar={() => setPagina((p) => Math.max(1, p - 1))}>
                anterior
              </Botao>
              <span>página {pagina.toLocaleString("pt-BR")} de {paginas.toLocaleString("pt-BR")}</span>
              <Botao desligado={pagina >= paginas} aoClicar={() => setPagina((p) => Math.min(paginas, p + 1))}>
                próxima
              </Botao>
            </span>
          ) : undefined
        }
      >
        {recado ?? (filtrando
          ? `${total.toLocaleString("pt-BR")} de 64.631 componentes`
          : "Todos os projetos. Filtre antes de exportar se quiser um recorte.")}
      </BarraDeEstado>
    </div>
  )
}

const rotulo: React.CSSProperties = {
  fontSize: 10, color: "var(--txt3)", textTransform: "uppercase", letterSpacing: .4,
}

const celula: React.CSSProperties = {
  padding: "5px 10px", borderBottom: "1px solid var(--line)", verticalAlign: "top",
}
